import { FC } from "react";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { SATOSLink } from "@/app/application/SATOSLink";
import { Translate } from "@/app/application/Translate";
import { useTrainingBack } from "@/features/training/hooks/useTrainingBack";
import type { FileUpload } from "@/features/training/model/Training/TrainingCourse";
import { SessionsList } from "@/features/training/pages/drill-tab/panel-right/drill/SessionsList";
import { CourseDetailSkeleton } from "@/features/training/pages/training-tab/CoursesSkeletons";
import { TrainingCourseActions } from "@/features/training/pages/training-tab/panel-right/training-course/TrainingCourseActions";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ImageIcon from "@mui/icons-material/Image";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
	Avatar,
	Box,
	Chip,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	Paper,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import {
	useFetchActiveSessionsByCourseId,
	useFetchArchivedSessionsByCourseId,
	useFetchCourse,
} from "@/service/TrainingQueries";

/* ---------- helpers ---------- */

const initials = (name?: string) =>
	(name || "")
		.split(" ")
		.filter(Boolean)
		.map((n) => n[0]?.toUpperCase())
		.slice(0, 2)
		.join("") || "—";

const humanizeDuration = (d?: string) =>
	d ? d.split(/(?=[A-Z])/).join(" ") : undefined;

const getFileIcon = (filename: string) => {
	const ext = filename.split(".").pop()?.toLowerCase();
	if (["pdf"].includes(ext || "")) return <PictureAsPdfIcon fontSize="small" />;
	if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext || ""))
		return <ImageIcon fontSize="small" />;
	return <DescriptionIcon fontSize="small" />;
};

/* ---------- component ---------- */

interface TrainingCourseProps {
	courseId: number;
}

export const TrainingCourse: FC<TrainingCourseProps> = ({ courseId }) => {
	const theme = useTheme();
	const handleBack = useTrainingBack();
	const { isLoading: isLoadingCourse, data: course } = useFetchCourse(courseId);

	if (isLoadingCourse || !course) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<CourseDetailSkeleton />
			</Box>
		);
	}

	const groups = course.GroupsWithCompetencyRequirement ?? [];
	const validityLabel = humanizeDuration(course.Duration) || "No expiry date";

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Compact header – aligned with TrainingDrill */}
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					gap: 2,
					py: 1.75,
					px: { xs: 2, sm: 2.5 },
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Tooltip
					title={<Translate String="Common.Back">Back</Translate>}
					enterDelay={400}
				>
					<IconButton
						onClick={handleBack}
						size="small"
						sx={{
							color: "primary.main",
							"&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
						}}
					>
						<ArrowBackIosIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>

				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography variant="h6" fontWeight={700} noWrap title={course.Name}>
						{course.Name}
					</Typography>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.5,
							mt: 0.5,
							flexWrap: "wrap",
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<Avatar
								sx={{
									width: 18,
									height: 18,
									bgcolor: alpha(theme.palette.primary.main, 0.12),
									color: "primary.main",
								}}
							>
								{initials(course.CourseManager?.Name)}
							</Avatar>
							<Typography variant="caption" color="text.secondary">
								{course.CourseManager?.Name || "—"}
							</Typography>
						</Box>
						<Typography variant="caption" color="text.disabled">
							·
						</Typography>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<FolderIcon sx={{ fontSize: 14, color: "text.secondary" }} />
							<Typography variant="caption" color="text.secondary">
								{validityLabel}
							</Typography>
						</Box>
					</Box>
				</Box>

				<TrainingCourseActions courseId={course.Id} />
			</Box>

			{/* Scrollable content: Overview + Sessions | Groups (pattern like CourseSession) */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					scrollbarGutter: "stable both-edges",
					overscrollBehavior: "contain",
					px: { xs: 2, sm: 2.5 },
				}}
			>
				{/* Overview: full width at top */}
				<Box sx={{ py: 1.75, borderBottom: 1, borderColor: "divider" }}>
					<Paper
						variant="outlined"
						sx={{
							p: 2,
							borderRadius: 2,
							borderColor: "divider",
						}}
					>
						<Typography
							variant="overline"
							color="text.secondary"
							sx={{ letterSpacing: 0.8 }}
						>
							<Translate String="Training.Drill.Overview">Overview</Translate>
						</Typography>

						{course.Description ? (
							<Box sx={{ mt: 1.5 }}>
								<SATOSContentRender
									isNotProcedureRender={true}
									content={course.Description}
								/>
							</Box>
						) : (
							<Typography
								variant="body2"
								color="text.secondary"
								fontStyle="italic"
								sx={{ mt: 1 }}
							>
								<Translate String="Common.NoDescription">
									No description provided.
								</Translate>
							</Typography>
						)}

						<Box
							sx={{
								mt: 2,
								display: "grid",
								gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
								gap: 2,
							}}
						>
							<Box>
								<Typography
									variant="caption"
									color="text.secondary"
									display="block"
								>
									<Translate String="Training.Course.Responsible">
										Responsible Trainer
									</Translate>
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{course.CourseManager?.Name || <em>—</em>}
								</Typography>
							</Box>
							<Box>
								<Typography
									variant="caption"
									color="text.secondary"
									display="block"
								>
									<Translate String="Training.Course.Validity">
										Validity
									</Translate>
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{humanizeDuration(course.Duration) || <em>—</em>}
								</Typography>
							</Box>
						</Box>

						{course.Attachments?.length ? (
							<Box sx={{ mt: 2 }}>
								<Typography
									variant="caption"
									color="text.secondary"
									display="block"
									sx={{ mb: 1 }}
								>
									<Translate String="Training.Course.Attachments">
										Attachments
									</Translate>
								</Typography>
								<List dense disablePadding>
									{course.Attachments.map((x: FileUpload) => (
										<Box
											key={x.Filename}
											sx={{ "& a": { textDecoration: "none" } }}
										>
											<SATOSLink to={x.UrlPath} target="_blank">
												<ListItem
													sx={{
														px: 0,
														py: 0.75,
														borderRadius: 1,
														"&:hover": {
															bgcolor: alpha(theme.palette.primary.main, 0.06),
														},
													}}
												>
													<ListItemIcon
														sx={{ minWidth: 32, color: "primary.main" }}
													>
														{getFileIcon(x.Filename)}
													</ListItemIcon>
													<Typography
														variant="body2"
														fontWeight={500}
														noWrap
														sx={{ flex: 1 }}
													>
														{x.Filename}
													</Typography>
													<OpenInNewIcon
														sx={{ fontSize: 14, color: "text.disabled" }}
													/>
												</ListItem>
											</SATOSLink>
										</Box>
									))}
								</List>
							</Box>
						) : null}
					</Paper>
				</Box>

				{/* Two columns: Sessions (left) | Groups (right) */}
				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
					}}
				>
					{/* Left: Sessions */}
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							borderRight: { md: 1 },
							borderBottom: { xs: 1, md: 0 },
							borderColor: "divider",
							px: 0,
							py: 1.75,
						}}
					>
						<SessionsList
							entityId={course.Id}
							entityName={course.Name}
							fetchActiveSessions={useFetchActiveSessionsByCourseId}
							fetchArchivedSessions={useFetchArchivedSessionsByCourseId}
							sectionTitle="Training.Session.Sessions"
							activeTitle="Training.Session.ActiveSessions"
							archivedTitle="Training.Session.ArchivedSessions"
							basePath={`/training/courses/${course.Id}/sessions`}
							compact
						/>
					</Box>

					{/* Right: Groups with competency requirement */}
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							px: 0,
							py: 1.75,
						}}
					>
						{groups.length > 0 ? (
							<Box sx={{ ml: 1.5 }}>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{ letterSpacing: 0.8, display: "block", mb: 1.5 }}
								>
									<Translate String="Training.Course.GroupsWithCompetencyRequirement">
										Groups with competency requirement
									</Translate>
									{groups.length > 0 && ` (${groups.length})`}
								</Typography>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
									{groups.map((g) => (
										<SATOSLink key={g.Id} to={`/groups/${g.Id}`}>
											<Chip
												clickable
												size="medium"
												label={g.Name}
												variant="outlined"
												sx={{
													fontWeight: 500,
													"&:hover": {
														bgcolor: alpha(theme.palette.primary.main, 0.08),
														borderColor: "primary.main",
													},
												}}
											/>
										</SATOSLink>
									))}
								</Box>
							</Box>
						) : (
							<Paper
								variant="outlined"
								sx={{
									p: 3,
									borderRadius: 2,
									textAlign: "center",
									borderColor: "divider",
									borderStyle: "dashed",
								}}
							>
								<GroupOutlinedIcon
									sx={{ fontSize: 40, color: "text.disabled", mb: 1.5 }}
								/>
								<Typography
									variant="body1"
									color="text.secondary"
									fontWeight={500}
								>
									<Translate String="Training.Course.Groups.Empty">
										No groups specified.
									</Translate>
								</Typography>
								<Typography
									variant="body2"
									color="text.disabled"
									sx={{ mt: 0.5 }}
								>
									<Translate String="Training.Course.Groups.Empty.Hint">
										Assign groups when editing this course.
									</Translate>
								</Typography>
							</Paper>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
